import { Routes } from '@angular/router';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { ForecastComponent } from './components/forecast/forecast.component';
import { ForecastCashFlowComponent } from './components/forecast-cash-flow/forecast-cash-flow.component';
import { ForecastCashFlowV2Component } from './components/forecast-cash-flow-v2/forecast-cash-flow-v2.component';
import { ForecastChurnComponent } from './components/forecast-churn/forecast-churn.component';
import { ForecastPipelineComponent } from './components/forecast-pipeline/forecast-pipeline.component';
import { CollectionsComponent } from './components/collections/collections.component';
import { SalesOperationsDashboardComponent } from './components/sales-operations/sales-operations-dashboard.component';
import { FintechDashboardComponent } from './components/fintech/fintech-dashboard.component';
import { CustomersListComponent } from './components/customers/customers-list.component';
import { CustomerFormComponent } from './components/customers/customer-form.component';
import { ProductsListComponent } from './components/products/products-list.component';
import { ProductFormComponent } from './components/products/product-form.component';
import { ProductTaxesListComponent } from './components/product-taxes/product-taxes-list.component';
import { ProductTaxFormComponent } from './components/product-taxes/product-tax-form.component';
import { InvoicesListComponent } from './components/invoices/invoices-list.component';
import { SalesListComponent } from './components/sales/sales-list.component';
import { ChargesCalendarComponent } from './components/charges/charges-calendar.component';
import { PaymentListComponent } from './components/payments/payment-list.component';
import { SaleFormComponent } from './components/sales/sale-form.component';
import { UsersListComponent } from './components/users/users-list.component';
import { UserFormComponent } from './components/users/user-form.component';
import { CompanyFormComponent } from './components/company/company-form.component';
import { CompanyConfigComponent } from './components/company/company-config.component';
import { CallbackComponent } from './components/callback/callback.component';
import { PaymentLinkSuccessComponent } from './components/callback/payment-link-success/payment-link-success.component';
import { NetpayPaymentLinkSuccessRedirectComponent } from './components/callback/netpay-payment-link-success-redirect/netpay-payment-link-success-redirect.component';
import { PublicInvoiceDetailComponent } from './components/public/public-invoice-detail/public-invoice-detail.component';
import { PublicReceiptComponent } from './components/public/public-receipt/public-receipt.component';
import { PublicSaleLinkReceiptComponent } from './components/public/public-sale-link-receipt/public-sale-link-receipt.component';
import { SaleLinksListComponent } from './components/sale-links/sale-links-list.component';
import { SaleLinkFormComponent } from './components/sale-links/sale-link-form.component';
import { MitMandatesListComponent } from './components/mit-mandates/mit-mandates-list.component';
import { MitMandateFormComponent } from './components/mit-mandates/mit-mandate-form.component';
import { MitMandateReadComponent } from './components/mit-mandates/mit-mandate-read.component';
import { MitTransactionsListComponent } from './components/mit-transactions/mit-transactions-list.component';
import { MitTransactionReadComponent } from './components/mit-transactions/mit-transaction-read.component';
import { WebhookFormComponent } from './components/webhooks/webhook-form.component';
import { WebhookListComponent } from './components/webhooks/webhook-list.component';
import { ReconciliationsListComponent } from './components/reconciliations/reconciliations-list.component';
import { ReconciliationDetailComponent } from './components/reconciliations/reconciliation-detail.component';
import { BankAccountsListComponent } from './components/bank-accounts/bank-accounts-list.component';
import { BankAccountFormComponent } from './components/bank-accounts/bank-account-form.component';
import { BankReconciliationsListComponent } from './components/bank-reconciliations/bank-reconciliations-list.component';
import { PlansListComponent } from './components/plans/plans-list.component';
import { PlanFormComponent } from './components/plans/plan-form.component';
import { SubscriptionsListComponent } from './components/subscriptions/subscriptions-list.component';
import { SubscriptionSaleFormComponent } from './components/subscriptions/subscription-sale-form.component';
import { PublicSubscriptionCheckoutComponent } from './components/public/public-subscription-checkout/public-subscription-checkout.component';
import { PublicNetpaySubscriptionCheckoutComponent } from './components/public/public-netpay-subscription-checkout/public-netpay-subscription-checkout.component';
import { PublicPaycodeSubscriptionCheckoutComponent } from './components/public/public-paycode-subscription-checkout/public-paycode-subscription-checkout.component';
import { PublicMitEnrollComponent } from './components/public/public-mit-enroll/public-mit-enroll.component';
import { PublicMitLimitsComponent } from './components/public/public-mit-limits/public-mit-limits.component';
import { PublicMitManageComponent } from './components/public/public-mit-manage/public-mit-manage.component';
import { ProfileComponent } from './components/profile/profile.component';
import { AuditListComponent } from './components/audit/audit-list.component';
import { ComunicadosListComponent } from './components/comunicados/comunicados-list.component';
import { CommunicationTemplatesListComponent } from './components/communication/communication-templates-list.component';
import { CommunicationTemplateFormComponent } from './components/communication/communication-template-form.component';
import { authGuard } from './guards/auth.guard';
import { homeRedirectGuard } from './guards/home-redirect.guard';
import { permissionGuard, type RoutePermissionData } from './guards/permission.guard';

/** Declara los roles Keycloak de la ruta. No se aplican hasta ENFORCE_ROUTE_PERMISSIONS. */
function perms(...permissions: string[]): RoutePermissionData {
  return { permissions };
}

export const routes: Routes = [
  // Rutas `callback/...` antes que `callback` para que no las capture el prefijo `callback`.
  {
    path: 'callback/paymentlink/success',
    component: PaymentLinkSuccessComponent
  },
  {
    path: 'callback/paycode/paymentlink/success',
    component: PaymentLinkSuccessComponent
  },
  {
    path: 'callback/netpay/paymentlink/success/:sessionId/:uuid',
    component: NetpayPaymentLinkSuccessRedirectComponent
  },
  {
    path: 'callback',
    component: CallbackComponent,
    pathMatch: 'full'
  },
  {
    path: 'public/invoices/:invoiceId',
    component: PublicInvoiceDetailComponent
  },
  {
    path: 'public/charges/:chargeId/receipt',
    component: PublicReceiptComponent
  },
  {
    path: 'public/sale-links/:id/receipt',
    component: PublicSaleLinkReceiptComponent
  },
  {
    path: 'public/sale-links/:id/subscription',
    component: PublicPaycodeSubscriptionCheckoutComponent
  },
  {
    path: 'public/subscriptions/netpay/:token',
    component: PublicNetpaySubscriptionCheckoutComponent
  },
  {
    path: 'public/subscriptions/paycode/:token',
    component: PublicPaycodeSubscriptionCheckoutComponent
  },
  {
    path: 'public/subscriptions/:token',
    component: PublicSubscriptionCheckoutComponent
  },
  {
    path: 'public/mit-mandates/:id/enroll',
    component: PublicMitEnrollComponent
  },
  {
    path: 'public/mit-mandates/:id/limits',
    component: PublicMitLimitsComponent
  },
  {
    path: 'public/mit-mandates/:id/manage',
    component: PublicMitManageComponent
  },

  {
    path: '',
    canActivate: [authGuard],
    canActivateChild: [permissionGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'forecasts/sales-operations'
      },
      {
        path: 'dashboard',
        component: DashboardComponent,
        data: perms('FORECAST_CASH_FLOW_READ')
      },
      {
        path: 'forecast',
        component: ForecastComponent,
        data: perms('FORECAST_CASH_FLOW_READ')
      },
      {
        path: 'forecasts/cash-flow',
        component: ForecastCashFlowComponent,
        data: perms('FORECAST_CASH_FLOW_READ')
      },
      {
        path: 'forecasts/cash-flow-v2',
        component: ForecastCashFlowV2Component,
        data: perms('FORECAST_CASH_FLOW_READ')
      },
      {
        path: 'forecasts/collection-calendar',
        component: ChargesCalendarComponent,
        data: perms('FORECAST_COLLECTION_CALENDAR_READ')
      },
      {
        path: 'forecast-churn',
        component: ForecastChurnComponent,
        data: perms('FORECAST_CHURN_READ')
      },
      {
        path: 'forecast-pipeline',
        component: ForecastPipelineComponent,
        data: perms('FORECAST_PIPELINE_READ')
      },
      {
        path: 'collections',
        component: CollectionsComponent,
        data: perms('CHARGE_SEARCH')
      },
      {
        path: 'aging',
        redirectTo: 'collections',
        pathMatch: 'full'
      },
      {
        path: 'forecasts/sales-operations',
        component: SalesOperationsDashboardComponent,
        data: perms('FORECAST_SALES_OPERATIONS_READ')
      },
      {
        path: 'fintech',
        component: FintechDashboardComponent,
        data: perms('FORECAST_CASH_FLOW_READ')
      },
      {
        path: 'company',
        component: CompanyFormComponent,
        data: perms('COMPANY_READ')
      },
      {
        path: 'configuracion',
        component: CompanyConfigComponent,
        data: perms('COMPANY_CONFIG_WRITE')
      },
      {
        path: 'profile',
        component: ProfileComponent
      },
      {
        path: 'customers/nuevo',
        component: CustomerFormComponent,
        data: perms('CUSTOMER_WRITE')
      },
      {
        path: 'customers/:id',
        component: CustomerFormComponent,
        data: perms('CUSTOMER_READ')
      },
      {
        path: 'customers',
        component: CustomersListComponent,
        data: perms('CUSTOMER_LIST')
      },
      {
        path: 'products/nuevo',
        component: ProductFormComponent,
        data: perms('PRODUCT_WRITE')
      },
      {
        path: 'products/:id',
        component: ProductFormComponent,
        data: perms('PRODUCT_READ')
      },
      {
        path: 'products',
        component: ProductsListComponent,
        data: perms('PRODUCT_LIST')
      },
      {
        path: 'product-taxes/nuevo',
        component: ProductTaxFormComponent,
        data: perms('PRODUCT_TAX_WRITE')
      },
      {
        path: 'product-taxes/:id',
        component: ProductTaxFormComponent,
        data: perms('PRODUCT_TAX_READ')
      },
      {
        path: 'product-taxes',
        component: ProductTaxesListComponent,
        data: perms('PRODUCT_TAX_LIST')
      },
      {
        path: 'invoices/nuevo',
        redirectTo: '/invoices',
        pathMatch: 'full'
      },
      {
        path: 'invoices/:id',
        redirectTo: '/invoices',
        pathMatch: 'full'
      },
      {
        path: 'invoices',
        component: InvoicesListComponent,
        data: perms('INVOICE_LIST')
      },
      {
        path: 'sales/nuevo',
        component: SaleFormComponent,
        data: perms('SALE_WRITE')
      },
      {
        path: 'sales/:id',
        component: SaleFormComponent,
        data: perms('SALE_READ')
      },
      {
        path: 'sales',
        component: SalesListComponent,
        data: perms('SALE_LIST')
      },
      {
        path: 'sale-links/nuevo',
        component: SaleLinkFormComponent,
        data: perms('SALE_LINK_WRITE')
      },
      {
        path: 'sale-links/:id',
        component: SaleLinkFormComponent,
        data: perms('SALE_LINK_READ')
      },
      {
        path: 'sale-links',
        component: SaleLinksListComponent,
        data: perms('SALE_LINK_LIST')
      },
      {
        path: 'mit-mandates/nuevo',
        component: MitMandateFormComponent,
        data: perms('MIT_MANDATE_WRITE')
      },
      {
        path: 'mit-mandates/:id',
        component: MitMandateReadComponent,
        data: perms('MIT_MANDATE_READ')
      },
      {
        path: 'mit-mandates',
        component: MitMandatesListComponent,
        data: perms('MIT_MANDATE_LIST')
      },
      {
        path: 'mit-transactions/:id',
        component: MitTransactionReadComponent,
        data: perms('MIT_TRANSACTION_READ')
      },
      {
        path: 'mit-transactions',
        component: MitTransactionsListComponent,
        data: perms('MIT_TRANSACTION_SEARCH')
      },
      {
        path: 'plans/nuevo',
        component: PlanFormComponent,
        data: perms('PLAN_WRITE')
      },
      {
        path: 'plans/:id',
        component: PlanFormComponent,
        data: perms('PLAN_READ')
      },
      {
        path: 'plans',
        component: PlansListComponent,
        data: perms('PLAN_LIST')
      },
      {
        path: 'subscriptions/nuevo',
        component: SubscriptionSaleFormComponent,
        data: perms('SUBSCRIPTION_WRITE')
      },
      {
        path: 'subscriptions/:id',
        component: SubscriptionSaleFormComponent,
        data: perms('SUBSCRIPTION_READ')
      },
      {
        path: 'subscriptions',
        component: SubscriptionsListComponent,
        data: perms('SUBSCRIPTION_LIST')
      },
      {
        path: 'collection-calendar',
        redirectTo: '/forecasts/collection-calendar',
        pathMatch: 'full'
      },
      {
        path: 'payments',
        component: PaymentListComponent,
        data: perms('PAYMENT_LIST')
      },
      {
        path: 'bank-accounts/nuevo',
        component: BankAccountFormComponent,
        data: perms('BANK_ACCOUNT_WRITE')
      },
      {
        path: 'bank-accounts/:id',
        component: BankAccountFormComponent,
        data: perms('BANK_ACCOUNT_READ')
      },
      {
        path: 'bank-accounts',
        component: BankAccountsListComponent,
        data: perms('BANK_ACCOUNT_LIST')
      },
      {
        path: 'bank-reconciliations',
        component: BankReconciliationsListComponent,
        data: perms('BANK_RECONCILIATION_LIST')
      },
      {
        path: 'reconciliations/:id',
        component: ReconciliationDetailComponent,
        data: perms('RECONCILIATION_READ')
      },
      {
        path: 'reconciliations',
        component: ReconciliationsListComponent,
        data: perms('RECONCILIATION_READ')
      },
      {
        path: 'users/nuevo',
        component: UserFormComponent,
        data: perms('USER_WRITE')
      },
      {
        path: 'users/:id',
        component: UserFormComponent,
        data: perms('USER_READ')
      },
      {
        path: 'users',
        component: UsersListComponent,
        data: perms('USER_SEARCH')
      },
      {
        path: 'audit',
        component: AuditListComponent,
        data: perms('AUDIT_READ')
      },
      {
        path: 'comunicacion/nuevo',
        component: CommunicationTemplateFormComponent,
        data: perms('COMMUNICATION_TEMPLATE_WRITE')
      },
      {
        path: 'comunicacion/:id',
        component: CommunicationTemplateFormComponent,
        data: perms('COMMUNICATION_TEMPLATE_WRITE')
      },
      {
        path: 'comunicacion',
        component: CommunicationTemplatesListComponent,
        data: perms('COMMUNICATION_TEMPLATE_SEARCH')
      },
      {
        path: 'comunicados',
        component: ComunicadosListComponent,
        data: perms('EMAIL_LIST')
      },
      {
        path: 'clientes',
        redirectTo: '/customers',
        pathMatch: 'full'
      },
      {
        path: 'productos',
        redirectTo: '/products',
        pathMatch: 'full'
      },
      {
        path: 'webhooks',
        component: WebhookListComponent,
        data: perms('WEBHOOK_READ')
      },
      {
        path: 'webhooks/new',
        component: WebhookFormComponent,
        data: perms('WEBHOOK_WRITE')
      },
      {
        path: 'webhooks/:id',
        component: WebhookFormComponent,
        data: perms('WEBHOOK_WRITE')
      },
      {
        path: 'webhooks/config',
        redirectTo: '/webhooks/new',
        pathMatch: 'full'
      },
      {
        path: '**',
        canActivate: [homeRedirectGuard],
        component: ProfileComponent
      }
    ]
  }
];
